// Public, login-free per-agent status page (Task 7.2).
// Renders OUTSIDE the AppShell and auth guards (see App.tsx route /status/:token),
// so the root element carries the `vitals` class itself to apply the design system.
// Ported from design/rev3 views-public-status.jsx.
//
// §11: this page renders ONLY status + uptime data from PublicStatusDto. It must NEVER
// render or request customer / provider / technician / contact / alert / GUID data —
// the DTO carries none, and we add none here. Data comes from publicGet (no Bearer).
import { useParams } from 'react-router-dom'
import { usePublicStatus } from '../api/hooks'
import { Icon } from '../components/icons'
import { UptimeBars } from '../components/UptimeBars'
import { APP_NAME } from '../config'
import type { PublicStatus as PublicStatusData } from '../api/types'
import { relativeFromNow, formatBerlin } from '../lib/time'

function fmtPct(pct: number | null | undefined): string {
  if (pct === null || pct === undefined) return '—'
  return `${pct.toFixed(1).replace('.', ',')} %`
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="vitals"
      style={{ minHeight: '100vh', background: 'var(--cream)', display: 'flex', flexDirection: 'column' }}
    >
      {children}
    </div>
  )
}

function CenteredMessage({ title, sub }: { title: string; sub?: string }) {
  return (
    <Shell>
      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
        <div style={{ textAlign: 'center', maxWidth: 420 }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--ink)' }}>{title}</div>
          {sub ? <div style={{ fontSize: 13.5, color: 'var(--ink-50)', marginTop: 6 }}>{sub}</div> : null}
        </div>
      </main>
    </Shell>
  )
}

function PSMetric({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div style={{ flex: 1, padding: '2px 4px' }}>
      <div className="vt-eyebrow" style={{ fontSize: 11.5 }}>{label}</div>
      <div className="vt-tnum" style={{ fontSize: 30, fontWeight: 600, marginTop: 5, letterSpacing: '-0.5px' }}>{value}</div>
      {sub ? <div style={{ fontSize: 12, color: 'var(--ink-50)', marginTop: 2 }}>{sub}</div> : null}
    </div>
  )
}

function StatusContent({ data, token }: { data: PublicStatusData; token: string }) {
  const online = data.online
  const tone = online
    ? { bg: 'var(--st-online-soft)', dot: 'var(--st-online)' }
    : { bg: 'var(--st-offline-soft)', dot: 'var(--st-offline)' }
  return (
    <Shell>
      {/* Kopf */}
      <header style={{ padding: '22px 32px', display: 'flex', alignItems: 'center', gap: 11, borderBottom: '1px solid var(--line)' }}>
        <div className="vt-brand-mark" style={{ background: 'var(--woodland)', color: 'var(--cream)' }}>
          <Icon.spark size={17} />
        </div>
        <div style={{ flex: 1 }}>
          <div className="vt-brand-name" style={{ color: 'var(--woodland)' }}>{APP_NAME}</div>
          <div className="vt-brand-sub">Status</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--ink-50)', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 20, padding: '6px 13px' }}>
          <Icon.link size={14} />
          <span className="vt-mono">/status/{token}</span>
        </div>
      </header>

      <main style={{ flex: 1, padding: '40px 32px 56px', display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 760 }}>
          {/* Status-Banner */}
          <div className="vt-panel" style={{ padding: 0, overflow: 'hidden', marginBottom: 22 }}>
            <div style={{ padding: '26px 28px', display: 'flex', alignItems: 'center', gap: 18, background: tone.bg, borderBottom: '1px solid var(--line)' }}>
              <span style={{ width: 44, height: 44, borderRadius: '50%', flex: '0 0 44px', background: tone.dot, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--on-accent)' }}>
                {online ? <Icon.check size={24} /> : <Icon.warn size={24} />}
              </span>
              <div>
                <div style={{ fontSize: 21, fontWeight: 600, letterSpacing: '-0.3px', color: 'var(--ink)' }}>
                  {online ? 'Betriebsbereit' : 'Störung'}
                </div>
                <div style={{ fontSize: 13.5, color: 'var(--ink-70)', marginTop: 2 }}>
                  {online ? 'Der Dienst im Monitoring antwortet normal.' : 'Der Dienst im Monitoring antwortet derzeit nicht.'}
                </div>
              </div>
              {data.lastKeepaliveAt ? (
                <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                  <div className="vt-eyebrow" style={{ fontSize: 11 }}>Letztes Lebenszeichen</div>
                  <div style={{ fontSize: 13.5, fontWeight: 500, marginTop: 2 }}>{relativeFromNow(data.lastKeepaliveAt)}</div>
                </div>
              ) : null}
            </div>
            <div style={{ padding: '16px 28px', display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <span className="vt-mono" style={{ fontSize: 15, fontWeight: 500 }}>{data.name}</span>
            </div>
          </div>

          {/* Kennzahlen */}
          <div className="vt-panel" style={{ padding: '20px 24px', marginBottom: 14, display: 'flex', gap: 8 }}>
            <PSMetric label="Verfügbarkeit · 30 Tage" value={fmtPct(data.uptime30)} />
            <div style={{ width: 1, background: 'var(--line)' }} />
            <PSMetric label="Verfügbarkeit · 90 Tage" value={fmtPct(data.uptime90)} />
            <div style={{ width: 1, background: 'var(--line)' }} />
            <PSMetric label="Vorfälle · 90 Tage" value={String(data.incidents90)} />
          </div>

          {/* Verlauf */}
          <div className="vt-panel" style={{ padding: '20px 24px', marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <span className="vt-eyebrow">Tägliche Verfügbarkeit · letzte 90 Tage</span>
              <span style={{ fontSize: 12, color: 'var(--ink-50)', display: 'flex', gap: 14 }}>
                <span className="vt-status online" style={{ fontSize: 12 }}><span className="vt-dot" />100 %</span>
                <span className="vt-status warn" style={{ fontSize: 12 }}><span className="vt-dot" />90–99,9 %</span>
                <span className="vt-status offline" style={{ fontSize: 12 }}><span className="vt-dot" />unter 90 %</span>
              </span>
            </div>
            <UptimeBars
              bars={data.bars90.map((b: PublicStatusData['bars90'][number]) => ({
                date: b.date,
                uptimePercent: b.uptimePercent,
                monitoredMinutes: 0,
                outageMinutes: 0,
                hasMaintenance: false,
              }))}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 7, fontSize: 11.5, color: 'var(--ink-35)' }}>
              <span>vor 90 Tagen</span><span>heute</span>
            </div>
          </div>

          {/* Wartung */}
          {data.maintenance ? (
            <div className="vt-card" style={{ padding: '15px 18px', display: 'flex', alignItems: 'flex-start', gap: 12, background: 'var(--surface-2)', borderColor: 'var(--line)', marginBottom: 14 }}>
              <Icon.wrench size={18} style={{ color: 'var(--ink-50)', flex: '0 0 auto', marginTop: 1 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)', marginBottom: 3 }}>Geplantes Wartungsfenster</div>
                {(data.maintenance.from || data.maintenance.to) ? (
                  <div style={{ fontSize: 13.5, color: 'var(--ink-70)', lineHeight: 1.5 }}>
                    {data.maintenance.from ? formatBerlin(data.maintenance.from) : '—'}
                    {' – '}
                    {data.maintenance.to ? formatBerlin(data.maintenance.to) : '—'}
                  </div>
                ) : null}
                {data.maintenance.note ? (
                  <div style={{ fontSize: 13, color: 'var(--ink-70)', lineHeight: 1.5, marginTop: 4 }}>{data.maintenance.note}</div>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* Mitteilungen (öffentlicher Text vom Agenten) */}
          {data.message ? (
            <div className="vt-panel" style={{ padding: '18px 22px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 9 }}>
                <Icon.note size={16} style={{ color: 'var(--ink-50)' }} />
                <h3 style={{ fontSize: 14.5, fontWeight: 600 }}>Mitteilungen</h3>
              </div>
              <p style={{ fontSize: 14, color: 'var(--ink-70)', lineHeight: 1.6 }}>{data.message}</p>
            </div>
          ) : null}

          <p style={{ textAlign: 'center', fontSize: 12.5, color: 'var(--ink-50)', marginTop: 30 }}>
            Bereitgestellt von <span style={{ fontWeight: 500, color: 'var(--ink-70)' }}>{APP_NAME}</span> · Status fortlaufend aktualisiert
          </p>
        </div>
      </main>
    </Shell>
  )
}

export default function PublicStatus() {
  const { token = '' } = useParams<{ token: string }>()
  const { data, isLoading, isError } = usePublicStatus(token)

  if (isLoading) return <CenteredMessage title="Lädt…" />
  if (isError || !data) {
    return (
      <CenteredMessage
        title="Statusseite nicht gefunden"
        sub="Diese Statusseite ist nicht verfügbar. Bitte prüfen Sie den Link."
      />
    )
  }
  return <StatusContent data={data} token={token} />
}
